import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { MarketAnalyticsSnapshot, MarketRegime } from '../types/marketAnalytics';
import {
  applyRegimeDocumentTheme,
  REGIME_BADGE_HIDDEN_KEY,
  regimeMeta,
} from '../utils/regimeTheme';
import { useMarketAnalytics } from '../hooks/useMarketAnalytics';
import { useLanguage } from './LanguageContext';
import { useAuth } from './AuthContext';
import { readStablePrimarySessionFirst } from '../utils/stableTabStorage';
import { canonicalUnderlyingKey } from '../utils/underlyingSymbolMatch';

interface RegimeContextValue {
  snapshot: MarketAnalyticsSnapshot | null;
  loading: boolean;
  error: string | null;
  regime: MarketRegime;
  refresh: () => void;
  badgeHidden: boolean;
  setBadgeHidden: (hidden: boolean) => void;
  regimeLabel: string;
  regimeEmoji: string;
  regimeAccent: string;
}

const RegimeContext = createContext<RegimeContextValue | undefined>(undefined);

export function useRegime(): RegimeContextValue {
  const ctx = useContext(RegimeContext);
  if (!ctx) throw new Error('useRegime must be used within RegimeProvider');
  return ctx;
}

export function useRegimeOptional(): RegimeContextValue | null {
  return useContext(RegimeContext) ?? null;
}

export const RegimeProvider: React.FC<{ children: React.ReactNode; symbol?: string }> = ({
  children,
  symbol,
}) => {
  const { language } = useLanguage();
  const ar = language === 'ar';

  const [activeSymbol, setActiveSymbol] = useState(() => {
    const raw = symbol || readStablePrimarySessionFirst() || 'SPY';
    return canonicalUnderlyingKey(raw) || raw.toUpperCase();
  });

  useEffect(() => {
    const sync = () => {
      const raw = symbol || readStablePrimarySessionFirst() || 'SPY';
      setActiveSymbol(canonicalUnderlyingKey(raw) || raw.toUpperCase());
    };
    sync();
    window.addEventListener('stableDataFetched', sync);
    window.addEventListener('underlyingChanged', sync);
    return () => {
      window.removeEventListener('stableDataFetched', sync);
      window.removeEventListener('underlyingChanged', sync);
    };
  }, [symbol]);

  const { token } = useAuth();
  const { snapshot, loading, error, refresh } = useMarketAnalytics(activeSymbol, token);
  const regime: MarketRegime = snapshot?.regime ?? 'CALM';
  const meta = regimeMeta(regime);

  const [badgeHidden, setBadgeHiddenState] = useState(() => {
    try {
      return localStorage.getItem(REGIME_BADGE_HIDDEN_KEY) === '1';
    } catch {
      return false;
    }
  });

  const setBadgeHidden = useCallback((hidden: boolean) => {
    setBadgeHiddenState(hidden);
    try {
      localStorage.setItem(REGIME_BADGE_HIDDEN_KEY, hidden ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    applyRegimeDocumentTheme(regime);
    return () => applyRegimeDocumentTheme(null);
  }, [regime]);

  const value = useMemo<RegimeContextValue>(
    () => ({
      snapshot,
      loading,
      error,
      regime,
      refresh,
      badgeHidden,
      setBadgeHidden,
      regimeLabel: ar ? meta.labelAr : meta.labelEn,
      regimeEmoji: meta.emoji,
      regimeAccent: meta.accent,
    }),
    [snapshot, loading, error, regime, refresh, badgeHidden, setBadgeHidden, ar, meta],
  );

  return <RegimeContext.Provider value={value}>{children}</RegimeContext.Provider>;
};
